import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  Stack,
  CircularProgress,
  LinearProgress,
  Divider,
} from '@mui/material';
import { PlaylistAdd as PlaylistAddIcon, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import axios from '../../../api/axios';
import PageHeader from '../../../components/Common/PageHeader';

const POLL_INTERVAL_MS = 1500;

function AddExercisesFlow() {
  const { notebookId } = useParams();
  const navigate = useNavigate();
  const [notebook, setNotebook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inputText, setInputText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [progressId, setProgressId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchNotebook() {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get(`/notes/${notebookId}`);
        if (cancelled) return;
        setNotebook(res.data);
      } catch (err) {
        if (!cancelled) {
          const msg = err.response?.data?.error || err.response?.data?.details || err.message || '加载笔记本失败，请重试';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchNotebook();
    return () => { cancelled = true; };
  }, [notebookId]);

  useEffect(() => {
    if (!progressId) return;
    const timer = setInterval(async () => {
      try {
        const res = await axios.get(`/notes/add-exercises-from-input/progress/${progressId}`);
        setProgress(res.data);
        if (res.data.stage === 'completed' || res.data.stage === 'error') {
          clearInterval(timer);
        }
      } catch (_) {}
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [progressId]);

  const handleAdd = async () => {
    const text = inputText.trim();
    if (!text) {
      setSubmitError('请输入要添加的题目内容');
      return;
    }

    setSubmitError(null);
    setProgress(null);
    setProgressId(null);
    setSubmitting(true);

    try {
      const res = await axios.post('/notes/add-exercises-from-input', {
        notebookId,
        inputText: text,
      });
      if (res.status === 202 && res.data.progressId) {
        setProgressId(res.data.progressId);
        setProgress({ stage: 'parsing', data: { message: '正在解析输入...' } });
      } else {
        setSubmitError('服务器未返回进度，请重试');
      }
    } catch (err) {
      setSubmitError(err.response?.data?.error || err.message || '发起失败');
    } finally {
      setSubmitting(false);
    }
  };

  const progressStage = progress?.stage;
  const progressData = progress?.data || {};
  const isDone = progressStage === 'completed' || progressStage === 'error';

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <PageHeader title="添加多道题目" backPath={() => navigate(`/notebook/${notebookId}?tab=skills`)} />
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>加载笔记本...</Typography>
        </Box>
      </Box>
    );
  }

  if (error || !notebook) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <PageHeader title="添加多道题目" backPath={() => navigate(-1)} />
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error || '笔记本不存在'}</Alert>
        </Box>
      </Box>
    );
  }

  const notebookTitle = notebook?.outline?.notebook_title || '笔记本';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column', pb: 2 }}>
      <PageHeader
        title="添加多道题目"
        backPath={() => navigate(`/notebook/${notebookId}?tab=skills`)}
      />
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'row',
          minHeight: 0,
          overflow: 'hidden',
          px: 2,
          py: 2,
          gap: 2,
        }}
      >
        {/* 左侧：状态栏（添加进度） */}
        <Paper
          sx={{
            width: '36%',
            minWidth: 260,
            maxWidth: 380,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" gutterBottom>
            {notebookTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Agent 会解析右侧输入内容，生成结构化题目并自动分配到对应章节。
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            当前状态
          </Typography>
          {!progressId && !isDone && (
            <Typography variant="body2" color="text.secondary">
              在右侧输入题目内容后，点击「添加题目」开始。
            </Typography>
          )}
          {progressId && !isDone && (
            <Box>
              <LinearProgress sx={{ mb: 1 }} />
              <Typography variant="body2">
                {progressData.message || '处理中...'}
              </Typography>
              {progressData.total != null && (
                <Typography variant="caption" color="text.secondary">
                  共 {progressData.total} 道题
                  {progressData.written != null && `，已写入 ${progressData.written} 道`}
                </Typography>
              )}
            </Box>
          )}
          {progressStage === 'completed' && (
            <Alert severity="success" icon={<CheckCircle />} sx={{ py: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                写入成功
              </Typography>
              {progressData.written != null && progressData.total != null && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {progressData.written === 0 && progressData.total > 0
                    ? '未成功写入任何题目，请查看后端日志或重试。'
                    : `已写入 ${progressData.written}/${progressData.total} 道题目到对应章节。`}
                </Typography>
              )}
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                可点击下方「管理题目」查看或编辑已添加的题目。
              </Typography>
            </Alert>
          )}
          {progressStage === 'error' && (
            <Alert severity="error" icon={<ErrorIcon />}>
              {progressData.message || '处理失败'}
            </Alert>
          )}
          {isDone && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <Button size="small" onClick={() => navigate(`/notebook/${notebookId}`)}>
                查看笔记
              </Button>
              <Button size="small" variant="outlined" onClick={() => navigate(`/questions/notebook/${notebookId}`)}>
                管理题目
              </Button>
            </Stack>
          )}
        </Paper>

        {/* 右侧：输入框 + 添加题目按钮 */}
        <Paper
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            overflow: 'hidden',
          }}
        >
          <TextField
            multiline
            minRows={18}
            maxRows={36}
            fullWidth
            placeholder={'在此输入或粘贴题目内容（可多道题、多行、多段）。Agent 会解析出每道题、选择题型、并分配到对应章节。\n\n例如：\n什么是递归？请写出斐波那契数列的递归实现。\n\n证明：若 n 为偶数则 n^2 为偶数。'}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            disabled={submitting}
            variant="outlined"
            sx={{
              flex: 1,
              '& .MuiInputBase-root': { height: '100%', alignItems: 'stretch' },
              '& .MuiInputBase-input': { height: '100% !important', overflow: 'auto !important', py: 2, px: 2 },
            }}
            inputProps={{ style: { minHeight: 360 } }}
          />
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderTop: 1, borderColor: 'divider' }}>
            <Button
              variant="contained"
              startIcon={submitting ? <CircularProgress size={20} /> : <PlaylistAddIcon />}
              onClick={handleAdd}
              disabled={!inputText.trim() || submitting}
            >
              {submitting ? '处理中...' : '添加题目'}
            </Button>
          </Box>
        </Paper>
      </Box>

      {submitError && (
        <Box sx={{ px: 2 }}>
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        </Box>
      )}
    </Box>
  );
}

export default AddExercisesFlow;
